import { supabase } from "./supabase";
import { myId } from "./me";
import type { DuplicateDeps } from "./duplicateWorkout";
import type { RemoveDeps, WorkoutUsage } from "./archiveWorkout";

/**
 * THE SUPABASE HALF OF DUPLICATING A WORKOUT — lifted out of `WorkoutOverviewWindow` in W32(7)
 * when the builder's `⋯` became a second caller.
 *
 * 🔴 IT IS SHARED RATHER THAN COPIED, AND THE REASON IS THE `p_client_id` NULL BELOW. A second
 * hand-written copy of these two callbacks would be two components deciding what "a template"
 * means against one RPC — the vocabulary-mismatch shape this repo is named after — and the half
 * that drifts is never the half anybody is looking at.
 *
 * ⚠️ `duplicateWorkout` ITSELF IS UNCHANGED AND STILL TAKES ITS DEPS. This module supplies them;
 * it does not become the function. The injection is what lets `duplicateWorkout.test.ts` drive
 * the rules without a database, and swallowing the seam here to save an argument would take that
 * away to save nothing.
 */
export const duplicateDeps: DuplicateDeps = {
  clone: async (id) => {
    const { data, error } = await supabase.rpc("clone_coach_workout", {
      p_workout_id: id,
      // ⚠️ NULL IS THE WHOLE POINT — `client_id is null` IS what makes the copy a template —
      // and the generated signature says `string`. That is a generator artefact rather than a
      // real constraint: every Postgres function argument is nullable, and the null was
      // executed against the live function in a rolled-back rehearsal before the library
      // row's own call was written. There is no legal uuid meaning "template".
      p_client_id: null as unknown as string,
    });
    return { id: data ?? null, error: error === null ? null : error.message };
  },
  rename: async (id, name) => {
    const { error } = await supabase
      .from("coach_workouts").update({ name, root_workout_id: null }).eq("id", id);
    return { error: error === null ? null : error.message };
  },
};

/**
 * The existing TEMPLATE names, read at click time.
 *
 * ⚠️ READ AT CLICK TIME RATHER THAN HELD, and that is better than holding them rather than merely
 * different: `duplicateName` only needs them to avoid a collision, and a list captured when a
 * page loaded can be stale by the time a coach decides. One `select` of fifteen names on a rare
 * action is cheap.
 *
 * ⚠️ AND A FAILURE TO READ THEM IS NOT A FAILURE TO DUPLICATE. An empty list makes `duplicateName`
 * fall back to the plain `<name> copy`, which may collide with a name already in the library — a
 * cosmetic outcome — whereas refusing the duplicate over it would be the annotation stopping the
 * thing a coach actually asked for. So this returns `[]` on an error and says nothing.
 */
export async function existingTemplateNames(): Promise<string[]> {
  const { data } = await supabase
    .from("coach_workouts").select("name").eq("coach_id", await myId()).is("client_id", null);
  return (data ?? []).map((n) => n.name);
}

/**
 * W85 (his report, 2026-09-27: *"don't we need a delete in the workouts at least in the 3 dots extra choices??"*) — THE
 * SUPABASE HALF OF DELETING A WORKOUT, lifted out of `WorkoutOverviewWindow` when the builder's `⋯` became a second caller,
 * for exactly the reason Duplicate's half was lifted above: two screens deciding what points at a template is how one of
 * them comes to read `Wrist series`'s fourteen direct assignments as zero. `removeWorkout` (the RULE, `archiveWorkout.ts`)
 * is unchanged and still takes its deps; this supplies them, once.
 *
 * WHAT POINTS AT THIS TEMPLATE, RIGHT NOW — two counts, one per foreign key that matters.
 * ⚠️ `head: true` WITH AN EXACT COUNT, so neither read drags rows across the wire; and they are two reads rather than one
 * join because the two questions live in different tables and a join would make a missing count indistinguishable from a
 * zero one.
 * ⚠️ AND THE ASSIGNMENT COUNT ASKS BOTH COLUMNS. An assignment names a template either through its copy's
 * `root_workout_id` or — for task rows, which are never copied — through `workout_id` directly. Asking only the first
 * reads 0 for `Wrist series`'s fourteen days.
 */
export async function readWorkoutUsage(workoutId: string): Promise<{ usage: WorkoutUsage | null; error: string | null }> {
  const copies = await supabase
    .from("coach_workouts").select("id", { count: "exact", head: true })
    .eq("root_workout_id", workoutId);
  if (copies.error !== null) return { usage: null, error: copies.error.message };
  const assigns = await supabase
    .from("coach_assignments").select("workout_id", { count: "exact", head: true })
    .or(`workout_id.eq.${workoutId},root_workout_id.eq.${workoutId}`);
  if (assigns.error !== null) return { usage: null, error: assigns.error.message };
  // ⚠️ A NULL COUNT IS NOT A ZERO. PostgREST returns null when it did not compute one, and treating that as "nothing
  // points at it" is precisely the reading that deletes history.
  if (copies.count === null || assigns.count === null) {
    return { usage: null, error: "the database did not return a count." };
  }
  return { usage: { copies: copies.count, assignments: assigns.count }, error: null };
}

/** The three calls `removeWorkout` takes — the ONE set, for every screen that deletes a workout. */
export const removeDeps: RemoveDeps = {
  usage: (id) => readWorkoutUsage(id),
  archive: async (id, atISO) => {
    const { error } = await supabase
      .from("coach_workouts").update({ archived_at: atISO }).eq("id", id);
    return { error: error === null ? null : error.message };
  },
  remove: async (id) => {
    const { error } = await supabase.from("coach_workouts").delete().eq("id", id);
    return { error: error === null ? null : error.message };
  },
};
